/**
 * Apply the S-52 layer set to a MapLibre map. One source per cell, the same
 * stack of layers for each, which is what gives us the quilt.
 *
 * Visibility is driven by:
 *   - display category toggles (Base / Standard / Other)
 *   - per-group toggles (depth-areas, soundings, buoyage, …)
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { ChartFeatureCollection } from '@/types';
import type { DeclutterLevel, Palette, SymbolSetId } from '@/store';
import { LAYERS, dryingCoverColours, MEDIUM_HIDDEN_LAYER_IDS, type DisplayCategory } from './style/layers';
import { annotateMarkDensity, annotateSoundingRank } from './markDensity';

export interface LayerVisibilityState {
  categories: Record<DisplayCategory, boolean>;
  groups: Record<string, boolean>;
  /** Declutter preset. At 'medium' a few Standard-category detail layers
   *  (MEDIUM_HIDDEN_LAYER_IDS) are hidden on top of the category rule. Optional so
   *  older callers default to 'full' (no extra hiding). */
  declutterLevel?: DeclutterLevel;
}

/**
 * Where in the layer stack a cell of this scale band belongs: BELOW the first
 * layer of any resident cell with a FINER band, so the quilt always stacks
 * coarse to fine no matter what order residency happens to load cells in.
 *
 * Without this, layers were appended in load order, and the order was whatever
 * the residency plan wanted that pass. Zoom in over a harbour and the plan
 * pulls the ocean-scale cells in as backdrop, which appended their island-wide
 * opaque land and depth fills ON TOP of the band-5 harbour detail. The chart
 * went blank as you zoomed IN, and which areas survived depended on the
 * history of adds, which is why no two attempts looked the same.
 *
 * Returns the layer id to pass as addLayer's beforeId, or undefined when this
 * cell is the finest resident, in which case appending is right (the overlay
 * layers above the cells are re-seated by their own ensure* machinery).
 */
export function cellAnchorLayerId(
  layers: { id: string }[],
  loadedBands: Map<string, number>,
  band: number,
): string | undefined {
  for (const layer of layers) {
    const m = /^cell-(.+?)-[a-z]/.exec(layer.id);
    if (!m) continue;
    const other = loadedBands.get(m[1]);
    // A cell layer whose band we cannot name is left alone rather than guessed at.
    if (other !== undefined && other > band) return layer.id;
  }
  return undefined;
}

/** Add a cell as a MapLibre GeoJSON source and stamp all S-52 layers onto it. */
export function addCellToMap(
  map: MaplibreMap,
  cellId: string,
  data: ChartFeatureCollection,
  palette: Palette,
  visibility: LayerVisibilityState,
  symbolSet: SymbolSetId,
  /** Insert the cell's layers before this layer (see cellAnchorLayerId). */
  beforeId?: string,
): void {
  const sourceId = `cell-${cellId}`;
  // Stamp the local-density hint so the buoy / beacon layers can keep a lone mark
  // on screen at low zoom while still thinning busy clusters.
  annotateMarkDensity(data);
  annotateSoundingRank(data);
  if (map.getSource(sourceId)) {
    // Replace data if the source already exists (e.g. style swap).
    (map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(data);
  } else {
    map.addSource(sourceId, {
      type: 'geojson',
      data,
      promoteId: 'id',
      // Mobile-friendly tiling. The cells are already geometry-simplified offline,
      // so we cap how hard MapLibre re-tiles them on the device: a lower maxzoom
      // means a shallower tile pyramid (less memory to build and hold), a smaller
      // buffer trims the duplicated edge geometry per tile, and a slightly higher
      // tolerance drops near-collinear points the phone would never show. Beyond
      // maxzoom the vector overzooms, so it stays crisp when zoomed right in.
      maxzoom: 14,
      buffer: 64,
      tolerance: 0.5,
    });
  }

  for (const def of LAYERS) {
    const layer = def.build(sourceId, palette, symbolSet);
    const visible = isVisible(def.category, def.group, visibility, def.id);
    const layerWithVisibility = {
      ...layer,
      layout: {
        ...('layout' in layer && layer.layout ? layer.layout : {}),
        visibility: visible ? ('visible' as const) : ('none' as const),
      },
    } as typeof layer;
    if (!map.getLayer(layer.id)) {
      map.addLayer(layerWithVisibility, beforeId);
    }
  }
}

/**
 * Remove a cell's source and every layer drawing from it.
 *
 * The layers are found by asking the style which ones point at this source,
 * rather than rebuilding their ids from LAYERS. Rebuilding only ever knew about
 * the S-52 stack, and two other modules hang their own layers off a cell source:
 * clearingDepthLayer stamps `cell-<id>-clrdep` on every one of them, and
 * contourHighlightLayer does the same. So a layer always survived, and MapLibre
 * refuses to drop a source that still has one, so it threw.
 *
 * That threw a long way. No cell was ever removed, so residency's cap counted
 * down while the map kept every source and its fifty-odd layers, which is the
 * exact unbounded growth residency exists to prevent. And because the drop runs
 * inside the moveend handler, the error came back out through the move and left
 * the map part-way through it.
 *
 * Asking the style also means the next module to hang a layer off a cell gets
 * cleaned up without having to know this function exists.
 */
export function removeCellFromMap(map: MaplibreMap, cellId: string): void {
  const sourceId = `cell-${cellId}`;
  // Collect the ids BEFORE removing any, because removeLayer mutates the very
  // list getStyle() hands back and iterating it while it shrinks skips layers.
  // getStyle() also returns undefined on a map that has already been removed,
  // which happens when a cell load is still in flight as the view unmounts.
  const doomed = (map.getStyle()?.layers ?? [])
    .filter((layer) => 'source' in layer && layer.source === sourceId)
    .map((layer) => layer.id);
  for (const id of doomed) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

export function isVisible(
  category: DisplayCategory,
  group: string,
  visibility: LayerVisibilityState,
  layerId?: string,
): boolean {
  if (!visibility.categories[category]) return false;
  // Medium declutter hides a few Standard-category detail layers (spot soundings,
  // light flares) even though their category is on — see MEDIUM_HIDDEN_LAYER_IDS.
  if (visibility.declutterLevel === 'medium' && layerId && MEDIUM_HIDDEN_LAYER_IDS.has(layerId)) {
    return false;
  }
  const g = visibility.groups[group];
  return g === undefined ? true : g;
}

/**
 * Re-skin every already-added cell layer for a new symbol set IN PLACE, without a
 * setStyle/full reload. Rebuilds each layer spec for the new set and pushes its
 * paint AND layout properties onto the live layers (per-set sea/area colours,
 * contour/coast widths, and per-set symbol fonts/glyphs/sizes), so switching Current / Paper / ECDIS re-paints the chart
 * immediately. Buoy glyphs are bitmap images, re-skinned separately via
 * setChartSymbolStyle. Returns false if the style isn't ready (caller defers).
 */
export function restyleCellsForSymbolSet(
  map: MaplibreMap,
  palette: Palette,
  symbolSet: SymbolSetId,
): boolean {
  // Gate on the style SPEC being parsed (getStyle() is undefined until then), NOT
  // on isStyleLoaded(): the running position sim calls setData on the ownship
  // source every tick, so a source is always loading and isStyleLoaded() is
  // permanently false, which would wrongly block live restyles.
  const style = map.getStyle();
  if (!style?.layers) return false;
  for (const def of LAYERS) {
    const spec = def.build('placeholder', palette, symbolSet);
    const suffix = spec.id.replace('placeholder', '');
    const paint = ('paint' in spec && spec.paint ? spec.paint : {}) as Record<string, unknown>;
    const layout = ('layout' in spec && spec.layout ? spec.layout : {}) as Record<string, unknown>;
    const paintKeys = Object.keys(paint);
    // Re-apply layout too (text-font, text-field, text-size, icon-size, ...) so a
    // set's per-feature symbol style switches live, not only its colours. Skip
    // 'visibility', which the category/group toggles own (applyVisibility).
    const layoutKeys = Object.keys(layout).filter((k) => k !== 'visibility');
    if (!paintKeys.length && !layoutKeys.length) continue;
    for (const layer of style.layers) {
      if (layer.id.startsWith('cell-') && layer.id.endsWith(suffix)) {
        for (const k of paintKeys) map.setPaintProperty(layer.id, k, paint[k] as never);
        for (const k of layoutKeys) map.setLayoutProperty(layer.id, k, layout[k] as never);
      }
    }
  }
  return true;
}

/** Re-apply visibility to every cell's layers in place. */
export function applyVisibility(map: MaplibreMap, visibility: LayerVisibilityState): void {
  // setStyle is async — getStyle() returns undefined until the style has loaded.
  // The visibility effect runs on mount as well as on store changes, so we have
  // to guard against that. (Gate on the parsed style spec, NOT isStyleLoaded() —
  // the running position sim keeps a source loading, so isStyleLoaded() is
  // permanently false and would wrongly keep the cells hidden on a source switch.)
  const style = map.getStyle();
  if (!style?.layers) return;

  // Work out what every layer SHOULD be first, then set only the ones that are
  // actually wrong.
  //
  // This used to call def.build() inside the inner loop and setLayoutProperty on
  // every match whether or not the value had changed. With nine cell sources
  // resident that is roughly a hundred layer defs against six hundred style
  // layers: sixty thousand full layer specs constructed (every filter and paint
  // expression, thrown away immediately) and sixty thousand setLayoutProperty
  // calls, each flagging MapLibre to revalidate. Toggling one overlay group made
  // the whole map go blank while it churned, which is fb-msbv96ux-0, "turning
  // TSS off or on temporarily makes all the charts disappear". The charts were
  // never hidden; the map simply could not paint.
  //
  // Same rule as before, so nothing about WHICH layers show has changed: match a
  // def by id suffix, and where a layer matches more than one def the last in
  // LAYERS order wins.
  const desired = new Map<string, 'visible' | 'none'>();
  for (const def of LAYERS) {
    // Built ONCE per def, not once per def per layer.
    const suffix = def.build('placeholder', 'day', 'current').id.replace('placeholder', '');
    const want = isVisible(def.category, def.group, visibility, def.id) ? 'visible' : 'none';
    for (const layer of style.layers) {
      if (layer.id.endsWith(suffix)) desired.set(layer.id, want);
    }
  }

  for (const layer of style.layers) {
    const want = desired.get(layer.id);
    if (!want) continue;
    // MapLibre treats a missing visibility as 'visible', so that is the default
    // we compare against; otherwise every already-visible layer would be set on
    // the first pass and we would be back where we started.
    const current = (layer.layout as { visibility?: string } | undefined)?.visibility ?? 'visible';
    if (current !== want) map.setLayoutProperty(layer.id, 'visibility', want);
  }
}

// The wreck/rock/obstruction layers whose low-zoom declutter embeds the user's
// safety depth (set via setDeclutterSafetyDepth in layers.ts). l-rock-danger
// (awash, surface-breaking) is deliberately excluded — it always shows early.
const HAZARD_DECLUTTER_LAYER_IDS = new Set(['l-wrecks', 'l-rock-submerged', 'l-obstruction-point']);

/**
 * Re-apply the hazard declutter filters in place. The filter expressions embed the
 * user's safety depth (a literal, captured at build time), so when the user changes
 * the safety depth we rebuild each hazard layer's filter and setFilter it live. The
 * relHazard precompute and the never-hidden-once-zoomed-in behaviour are unchanged.
 */
export function applyHazardDeclutter(map: MaplibreMap): void {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const def of LAYERS) {
    if (!HAZARD_DECLUTTER_LAYER_IDS.has(def.id)) continue;
    const spec = def.build('placeholder', 'day', 'current');
    const suffix = spec.id.replace('placeholder', '');
    const filter = 'filter' in spec ? spec.filter : undefined;
    if (!filter) continue;
    for (const layer of style.layers) {
      if (layer.id.startsWith('cell-') && layer.id.endsWith(suffix)) {
        map.setFilter(layer.id, filter as never);
      }
    }
  }
}

// The buoy / beacon layers whose filters embed the declutter level (set via
// setDeclutterLevel in layers.ts): at 'medium' they show only the significant
// marks, so the filter has to be rebuilt live when the level changes.
const DECLUTTER_MARK_LAYER_IDS = new Set(['l-buoy-dot', 'l-beacon-dot']);

/**
 * Re-apply the buoy / beacon filters in place after the declutter level changes,
 * so switching to Medium drops the minor marks (and switching back restores them)
 * without a full reload. The level is a literal captured at build time, so we
 * rebuild each mark layer's filter and setFilter it live. Visibility-based hides
 * (spot soundings, light flares) go through applyVisibility, not here.
 */
export function applyDeclutterMarks(map: MaplibreMap): void {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const def of LAYERS) {
    if (!DECLUTTER_MARK_LAYER_IDS.has(def.id)) continue;
    const spec = def.build('placeholder', 'day', 'current');
    const suffix = spec.id.replace('placeholder', '');
    const filter = 'filter' in spec ? spec.filter : undefined;
    if (!filter) continue;
    for (const layer of style.layers) {
      if (layer.id.startsWith('cell-') && layer.id.endsWith(suffix)) {
        map.setFilter(layer.id, filter as never);
      }
    }
  }
}

// The depth-area band layers whose filters embed the user's shading contours
// (set via setDepthShading in layers.ts).
const DEPTH_BAND_LAYER_IDS = new Set(['l-depare-deep', 'l-depare-medium', 'l-depare-shallow']);

/**
 * Re-apply the depth-area band filters in place after the user changes the shading
 * contours. The filters bin DEPARE areas by depth against thresholds that are
 * literals captured at build time, so we rebuild each band's filter and setFilter
 * it live; the per-set colours are untouched.
 */
export function applyDepthBands(map: MaplibreMap): void {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const def of LAYERS) {
    if (!DEPTH_BAND_LAYER_IDS.has(def.id)) continue;
    const spec = def.build('placeholder', 'day', 'current');
    const suffix = spec.id.replace('placeholder', '');
    const filter = 'filter' in spec ? spec.filter : undefined;
    if (!filter) continue;
    for (const layer of style.layers) {
      if (layer.id.startsWith('cell-') && layer.id.endsWith(suffix)) {
        map.setFilter(layer.id, filter as never);
      }
    }
  }
}

/** The list of unique group names, in render order. */
export function listLayerGroups(): { group: string; category: DisplayCategory }[] {
  const seen = new Map<string, DisplayCategory>();
  for (const l of LAYERS) {
    if (!seen.has(l.group)) seen.set(l.group, l.category);
  }
  return Array.from(seen, ([group, category]) => ({ group, category }));
}

/**
 * Shade drying areas by whether the tide has covered them now. A drying patch
 * dries to a height of (−depthMinM) above chart datum, so it is covered when the
 * height of tide exceeds that, i.e. (heightNow + depthMinM) > 0. Covered patches
 * take the shallow-water colour; uncovered ones keep the drying tint. Applied in
 * place to the live `*-depare-drying` layers (re-applied after any re-style).
 * heightNow null is treated as chart datum (0), so nothing reads as covered.
 */
export function updateDryingCover(
  map: MaplibreMap,
  heightNowM: number | null,
  palette: Palette,
  symbolSet: SymbolSetId,
): void {
  // Style-spec-ready gate (not isStyleLoaded(), which the sim keeps false).
  const style = map.getStyle();
  if (!style?.layers) return;
  const { drying, covered } = dryingCoverColours(palette, symbolSet);
  const h = heightNowM ?? 0;
  const expr = [
    'case',
    ['>', ['+', h, ['coalesce', ['get', 'depthMinM', ['get', 'attrs']], 0]], 0],
    covered,
    drying,
  ] as unknown;
  for (const layer of style.layers) {
    if (layer.id.startsWith('cell-') && layer.id.endsWith('-depare-drying')) {
      map.setPaintProperty(layer.id, 'fill-color', expr as never);
    }
  }
}
